// import the api endpoints
import Register from '@/services/register.api';
import Users from '@/services/users.api';
import Promotions from '@/services/promotions.api';
import { setCookieJSON, getCookieJSON, removeCookieJSON } from '@/utils/cookie';

const state = {
  registrationStep: '',
  documentTypeId: null,
  paymentData: {},
  socialLogin: {},
  userLogin: {},
  metamap: {},
  token: null,
};

const mutations = {
  UPDATE_PAYMENT_DATA(state, paymentData) {
    state.paymentData = paymentData;
    setCookieJSON(
      'payment',
      {
        paymentMethodId: paymentData.paymentMethodId,
      },
      { expires: '60m' },
    );
  },
  UPDATE_REGISTRATION_STEP(state, registrationStep) {
    state.registrationStep = registrationStep;
    setCookieJSON(
      'step',
      {
        registrationStep,
      },
      { expires: '60m' },
    );
  },
  UPDATE_DOCUMENT_TYPE_ID(state, documentTypeId) {
    state.documentTypeId = documentTypeId;
  },
  UPDATE_SOCIAL_LOGIN(state, socialLogin) {
    state.socialLogin = socialLogin;
    setCookieJSON('social', { socialLogin }, { expires: '60m' });
  },
  UPDATE_USER_LOGIN(state, user) {
    state.userLogin = user;
  },
  SET_TOKEN(state, token) {
    state.token = token;
    setCookieJSON('token', token, { expires: '60m' });
  },
  UPDATE_METAMAP(state, metamap) {
    state.metamap = metamap;
  },
};

const getters = {
  registrationStep(state) {
    return state.registrationStep;
  },
  userToken(state) {
    return state.token;
  },
  documentTypeId(state) {
    return state.documentTypeId;
  },
  paymentData(state) {
    return state.paymentData;
  },
  getSocialLogin() {
    return getCookieJSON('social');
  },
  getLogin(state) {
    return state.userLogin;
  },
  getPaymentMethodId() {
    const payment = getCookieJSON('payment');
    removeCookieJSON('payment');
    return payment;
  },
  getMetamap() {
    return state.metamap;
  },
};

const actions = {
  login({ commit }, user) {
    return Users.login(user)
      .then(resp => {
        const { data } = resp;

        commit('UPDATE_REGISTRATION_STEP', data.status);

        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  gowgoLogin({ commit }, login) {
    return Users.gowgoLogin(login)
      .then(resp => {
        const { data } = resp;
        let step =
          data.registrationStep === 'NONE'
            ? 'CREATE_USER'
            : data.registrationStep;
        commit('UPDATE_REGISTRATION_STEP', step);
        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  saveMetamap({ commit }, metamapData) {
    commit('UPDATE_METAMAP', metamapData);
    return metamapData;
  },
  saveLogin({ commit }, user) {
    commit('UPDATE_USER_LOGIN', user);
  },
  socialLogin({ commit }, socialLogin) {
    return Register.socialLogin({
      socialLoginType: socialLogin.socialLoginType,
      token: socialLogin.token,
    })
      .then(resp => {
        const { data } = resp;
        commit('UPDATE_SOCIAL_LOGIN', socialLogin);
        let step =
          data.registrationStep === 'NONE'
            ? 'CREATE_USER'
            : data.registrationStep;
        commit('UPDATE_REGISTRATION_STEP', step);

        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  profile({ commit }) {
    return Users.profile()
      .then(resp => {
        const { data } = resp;

        if (data.documentTypeId) {
          let documentTypeId = 3;
          switch (data.documentTypeId) {
            case 'CHL1':
              documentTypeId = 1;
              break;
            case 'CHL2':
              documentTypeId = 2;
          }
          commit('UPDATE_DOCUMENT_TYPE_ID', documentTypeId);
        }

        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  createUser({ commit }, user) {
    return Register.createUser(user)
      .then(resp => {
        const { data } = resp;

        commit('UPDATE_REGISTRATION_STEP', data.registrationStep);

        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  savePhone({ commit }, phone) {
    return Register.phoneNumber(phone)
      .then(resp => {
        const { data } = resp;

        commit('UPDATE_REGISTRATION_STEP', data.registrationStep);

        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  phoneRetry() {
    return Register.phoneRetry()
      .then(resp => {
        const { data } = resp;

        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  phoneVerify({ commit }, code) {
    return Register.phoneVerify(code)
      .then(resp => {
        const { data } = resp;

        commit('UPDATE_REGISTRATION_STEP', data.registrationStep);

        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  documentTypes() {
    return Register.documentTypes()
      .then(resp => {
        const { data } = resp;

        const options = data.documentTypes.map(option => {
          const code = option.documentType.includes('CHL') ? 'cl' : 'world';

          return {
            ...option,
            code,
          };
        });

        return options;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  document({ commit }, document) {
    commit('UPDATE_DOCUMENT_TYPE_ID', document.documentTypeId);
    return Register.document(document)
      .then(resp => {
        const { data } = resp;

        commit('UPDATE_REGISTRATION_STEP', data.registrationStep);

        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  manualDocument({ commit }, files) {
    return Register.manualDocument(files)
      .then(resp => {
        const { data } = resp;

        commit('UPDATE_REGISTRATION_STEP', data.registrationStep);

        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  manualDocumentSelfie({ commit }, files) {
    return Register.manualDocumentSelfie(files)
      .then(resp => {
        const { data } = resp;

        commit('UPDATE_REGISTRATION_STEP', data.registrationStep);

        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  documentDrive({ commit }, files) {
    return Register.documentDrive(files)
      .then(resp => {
        const { data } = resp;

        commit('UPDATE_REGISTRATION_STEP', data.registrationStep);

        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  tocId({ commit }, files) {
    return Register.tocId(files)
      .then(resp => {
        const { data } = resp;

        commit('UPDATE_REGISTRATION_STEP', data.registrationStep);

        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  tocSelfie({ commit }, files) {
    return Register.tocSelfie(files)
      .then(resp => {
        const { data } = resp;

        commit('UPDATE_REGISTRATION_STEP', data.registrationStep);

        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  getGeneralInformation({ commit }) {
    return Register.getGeneralInformation()
      .then(resp => {
        const { data } = resp;

        commit('UPDATE_REGISTRATION_STEP', data.nextStep);

        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  generalInformation({ commit }, userInformation) {
    return Register.generalInformation(userInformation)
      .then(resp => {
        const { data } = resp;

        commit('UPDATE_REGISTRATION_STEP', data.registrationStep);

        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  getMembership() {
    return Register.getMembership()
      .then(resp => {
        const { data } = resp;

        return data.memberships;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  membership({ commit }, membershipId) {
    return Register.membership(membershipId)
      .then(resp => {
        const { data } = resp;

        commit('UPDATE_REGISTRATION_STEP', data.registrationStep);

        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  getTocSession() {
    return Register.getTocSession()
      .then(resp => {
        const { data } = resp;

        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  goToManual({ commit }) {
    return Register.goToManual()
      .then(resp => {
        const { data } = resp;

        commit('UPDATE_REGISTRATION_STEP', data.registrationStep);

        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  setRegistrationStep({ commit }, registrationStep) {
    commit('UPDATE_REGISTRATION_STEP', registrationStep);
  },
  getPaymentList() {
    return Register.getPaymentList()
      .then(resp => {
        const { data } = resp;

        return data.listPaymentResponse;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  getRegistrationStep({ commit }) {
    return Register.getRegistrationStep()
      .then(resp => {
        const { data } = resp;
        commit('UPDATE_REGISTRATION_STEP', data.registrationStep);
        return data.registrationStep;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  getPaymentData({ commit }, paymentMethodId) {
    return Register.getPaymentData(paymentMethodId)
      .then(resp => {
        const { data } = resp;
        if (paymentMethodId === 1 || paymentMethodId === 4) {
          data.paymentMethodId = paymentMethodId;
          commit('UPDATE_PAYMENT_DATA', data);
        } else if (paymentMethodId === 3) {
          commit('UPDATE_REGISTRATION_STEP', data.registrationStep);
        }
        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  referral(_store, code) {
    return Promotions.referral(code)
      .then(resp => {
        const { data } = resp;
        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  getCsrf() {
    return Promotions.getCsrf()
      .then(resp => {
        const { data } = resp;
        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  resetPassword(_store, email) {
    return Register.resetPassword(email)
      .then(resp => {
        const { data } = resp;
        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  resetPasswordValidateUser(_store, token) {
    return Register.resetPasswordValidateUser(token)
      .then(resp => {
        const { data } = resp;
        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  changePassword(_store, changePassword) {
    return Register.changePassword(changePassword)
      .then(resp => {
        const { data } = resp;
        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  setPin(_store, pin) {
    return Users.setPin(pin)
      .then(resp => {
        const { data } = resp;
        return data;
      })
      .catch(error => {
        throw new Error(error);
      });
  },
  setToken({ commit }, token) {
    commit('SET_TOKEN', token);
    setCookieJSON('token', token, { expires: '60m' });
  },
  checkCookies({ commit }) {
    const token = getCookieJSON('token');
    const step = getCookieJSON('step');
    if (token) commit('SET_TOKEN', token);
    if (step) commit('UPDATE_REGISTRATION_STEP', step.registrationStep);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
